/**
 * Trinity Triangle Background System
 *
 * Animated triangle backgrounds for Trinity Agency branding.
 * Renders a grid of triangles with configurable animations.
 *
 * Uses tri-cube coordinate system (Boris the Brave's formulas)
 * for correct IVM topology.
 */

import type { TriCubeCoord } from './core/types';
import { TRINITY_PALETTE } from './core/types';
import { fromAxial } from './core/tri-cube';
import { seed } from './noise';
import {
  animations,
  type AnimationType,
  type AnimationConfig,
  type AnimationContext,
  type AnimationState,
  type TrianglePosition,
} from './animations';
import { CascadeSystem, type CascadeConfig, type CoveragePreset, type TriangleInfo } from './cascade';
import { IVMEngine } from './ivm-engine';
import { IVM_PRESET_MAP, IVM_FILTER_MAP } from './ivm-presets';

const SQRT3 = Math.sqrt(3);

export interface BackgroundConfig {
  // Grid
  sideLength: number;
  gap: number;

  // Colors
  colors: string[];
  colorMode: 'solid' | 'gradient' | 'random' | 'position';

  // Animation
  animation: AnimationType | 'cascade' | 'ivm';
  animationConfig: Partial<AnimationConfig>;
  cascadeConfig: Partial<CascadeConfig>;
  ivmPreset?: string;
  ivmFilter?: string;

  // Rendering
  backgroundColor: string;
  strokeColor: string;
  strokeWidth: number;
  baseOpacity: number;

  // Performance
  maxTriangles: number;
}

const DEFAULT_CONFIG: BackgroundConfig = {
  sideLength: 40,
  gap: 2,

  colors: [
    TRINITY_PALETTE.lightBlue,
    TRINITY_PALETTE.mediumBlue,
    TRINITY_PALETTE.darkBlue,
    TRINITY_PALETTE.deepBlue,
  ],
  colorMode: 'position',

  animation: 'swarm',
  animationConfig: {
    speed: 1,
    intensity: 0.6,
    noiseScale: 2.5,
    mouseRadius: 0.12,
    mouseStrength: 0.9,
  },
  cascadeConfig: {
    flipDuration: 800,
    flipChance: 0.003,
    cascadeChance: 0.15,
    cascadeDelay: 150,
    coverage: 'vignette',
    coverageFalloff: 0.15,
  },

  backgroundColor: '#0a0f1a',
  strokeColor: 'rgba(30, 41, 59, 0.5)',
  strokeWidth: 0.5,
  baseOpacity: 0.85,

  maxTriangles: 0, // 0 = auto (compute from viewport)
};

interface TriangleData {
  coord: TriCubeCoord;
  centerX: number;
  centerY: number;
  normalizedX: number;
  normalizedY: number;
  orientation: 'up' | 'down';
  color: string;
  index: number;
}

export class BackgroundSystem {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private config: BackgroundConfig;
  private animationConfig: AnimationConfig;

  private triangles: TriangleData[] = [];
  private coordIndexMap: Map<string, number> = new Map();
  private width = 0;
  private height = 0;
  private startTime = 0;
  private animationId: number | null = null;
  private isRunning = false;
  private prevFrameTime = 0;

  // Mouse state
  private mouseX = 0.5;
  private mouseY = 0.5;
  private mouseActive = false;

  // Cascade animation system
  private cascadeSystem: CascadeSystem;

  // IVM simulation engine
  private ivmEngine = new IVMEngine();

  // Performance
  private lastFrameTime = 0;
  private frameCount = 0;
  private fps = 60;

  constructor(canvas: HTMLCanvasElement, config: Partial<BackgroundConfig> = {}) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('Could not get 2D context');
    this.ctx = ctx;

    this.config = { ...DEFAULT_CONFIG, ...config };
    this.animationConfig = {
      speed: 1,
      intensity: 0.5,
      noiseScale: 2,
      mouseRadius: 0.15,
      mouseStrength: 0.8,
      ...this.config.animationConfig,
    };

    // Initialize cascade system
    this.cascadeSystem = new CascadeSystem(this.config.cascadeConfig);

    // Initialize noise with random seed
    seed(Math.random() * 10000);

    // Apply IVM preset / filter from the initial config. setConfig() does
    // this on later updates, but on first mount the engine would otherwise
    // run with a null preset — visuals fall back to the default triangle
    // palette (blue) and update() no-ops, so nothing animates.
    if (this.config.ivmPreset) {
      this.ivmEngine.setPreset(IVM_PRESET_MAP.get(this.config.ivmPreset) ?? null);
    }
    if (this.config.ivmFilter) {
      this.ivmEngine.setFilter(IVM_FILTER_MAP.get(this.config.ivmFilter) ?? null);
    }

    this.setupEventListeners();
    this.handleResize();

    // Auto-start
    this.start();
  }

  /**
   * Set up event listeners
   */
  private setupEventListeners(): void {
    // Mouse tracking
    this.canvas.addEventListener('mousemove', (e) => {
      const rect = this.canvas.getBoundingClientRect();
      this.mouseX = (e.clientX - rect.left) / rect.width;
      this.mouseY = (e.clientY - rect.top) / rect.height;
      this.mouseActive = true;
    });

    this.canvas.addEventListener('mouseleave', () => {
      this.mouseActive = false;
    });

    this.canvas.addEventListener('mouseenter', () => {
      this.mouseActive = true;
    });

    // Touch support
    this.canvas.addEventListener('touchmove', (e) => {
      if (e.touches.length > 0) {
        const rect = this.canvas.getBoundingClientRect();
        this.mouseX = (e.touches[0].clientX - rect.left) / rect.width;
        this.mouseY = (e.touches[0].clientY - rect.top) / rect.height;
        this.mouseActive = true;
      }
    });

    this.canvas.addEventListener('touchend', () => {
      this.mouseActive = false;
    });

    // Resize observer
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => this.handleResize());
      observer.observe(this.canvas.parentElement || this.canvas);
    } else {
      window.addEventListener('resize', () => this.handleResize());
    }
  }

  /**
   * Handle canvas resize
   */
  private handleResize(): void {
    const container = this.canvas.parentElement || document.body;
    const rect = container.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    this.width = rect.width;
    this.height = rect.height;

    this.canvas.width = this.width * dpr;
    this.canvas.height = this.height * dpr;
    this.canvas.style.width = `${this.width}px`;
    this.canvas.style.height = `${this.height}px`;

    this.ctx.scale(dpr, dpr);

    this.generateGrid();
  }

  /**
   * Generate the triangle grid using tri-cube coordinates.
   *
   * IVM origin at canvas center. Axial (row, col) iterated to cover viewport,
   * then each converted to TriCubeCoord via fromAxial() and positioned via
   * toPixelCentered() with canvas-center offset.
   */
  private generateGrid(): void {
    this.triangles = [];
    this.coordIndexMap.clear();

    const { sideLength, gap, colors, colorMode, maxTriangles } = this.config;

    // effectiveSide governs coordinate spacing (includes gap)
    const effectiveSide = sideLength + gap;

    // Row height and column width in pixel space
    const h = effectiveSide * SQRT3 / 2;
    const halfCol = effectiveSide / 2;

    // IVM origin at canvas center
    const halfW = this.width / 2;
    const halfH = this.height / 2;

    // Axial ranges to cover viewport with padding
    const rowMin = Math.floor(-halfH / h) - 1;
    const rowMax = Math.ceil(halfH / h) + 1;
    const colMin = Math.floor(-halfW / halfCol) - 1;
    const colMax = Math.ceil(halfW / halfCol) + 1;

    // Dynamic safety cap: 0 = auto from viewport, otherwise use configured value
    const triArea = effectiveSide * effectiveSide * SQRT3 / 4;
    const viewportArea = this.width * this.height;
    const cap = maxTriangles > 0 ? maxTriangles : Math.ceil(viewportArea / triArea * 2.5);

    let index = 0;

    for (let row = rowMin; row <= rowMax && index < cap; row++) {
      for (let col = colMin; col <= colMax && index < cap; col++) {
        const coord = fromAxial(row, col);
        const isUp = (row + col) % 2 === 0;

        // Screen-aligned pixel position: rows horizontal, cols vertical
        // tri-cube coord used only for topology (neighbors, orientation)
        const cx = halfW + col * halfCol;
        const cy = halfH + row * h + (isUp ? h * 2 / 3 : h / 3);

        // Skip triangles too far outside viewport
        if (cx < -sideLength * 2 || cx > this.width + sideLength * 2) continue;
        if (cy < -sideLength * 2 || cy > this.height + sideLength * 2) continue;

        const orient = isUp ? 'up' as const : 'down' as const;

        // Determine color
        let color: string;
        switch (colorMode) {
          case 'solid':
            color = colors[0];
            break;
          case 'random':
            color = colors[Math.floor(Math.random() * colors.length)];
            break;
          case 'gradient': {
            const t = cx / this.width;
            const idx = Math.floor(t * (colors.length - 1));
            color = colors[Math.min(idx, colors.length - 1)];
            break;
          }
          case 'position':
          default: {
            // Create depth effect - darker at edges, lighter in center
            const nx = cx / this.width;
            const ny = cy / this.height;
            const centerDist = Math.sqrt((nx - 0.5) ** 2 + (ny - 0.5) ** 2);
            const colorIdx = Math.floor(centerDist * 2 * colors.length) % colors.length;
            color = colors[colorIdx];
            break;
          }
        }

        const coordKey = `${coord.a},${coord.b},${coord.c}`;
        this.coordIndexMap.set(coordKey, index);

        this.triangles.push({
          coord,
          centerX: cx,
          centerY: cy,
          normalizedX: cx / this.width,
          normalizedY: cy / this.height,
          orientation: orient,
          color,
          index,
        });

        index++;
      }
    }

    // Initialize cascade system with triangle data and coord lookup
    const triInfos: TriangleInfo[] = this.triangles.map(t => ({
      coord: t.coord,
      normalizedX: t.normalizedX,
      normalizedY: t.normalizedY,
    }));
    this.cascadeSystem.init(this.triangles.length, this.coordIndexMap, triInfos);

    // Initialize IVM engine with triangle data
    this.ivmEngine.init(this.triangles.length, this.coordIndexMap, this.triangles);
  }

  /**
   * Start the animation loop
   */
  start(): void {
    if (this.isRunning) return;
    this.isRunning = true;
    this.startTime = performance.now();
    this.lastFrameTime = this.startTime;
    this.prevFrameTime = this.startTime;
    this.animate();
  }

  /**
   * Stop the animation loop
   */
  stop(): void {
    this.isRunning = false;
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  /**
   * Main animation loop
   */
  private animate = (): void => {
    if (!this.isRunning) return;

    const now = performance.now();
    const elapsed = (now - this.startTime) / 1000;
    const deltaTime = now - this.prevFrameTime;
    this.prevFrameTime = now;

    // FPS calculation
    this.frameCount++;
    if (now - this.lastFrameTime >= 1000) {
      this.fps = this.frameCount;
      this.frameCount = 0;
      this.lastFrameTime = now;
    }

    // Update cascade system if using cascade animation
    if (this.config.animation === 'cascade') {
      this.cascadeSystem.update(now, deltaTime);
    } else if (this.config.animation === 'ivm') {
      this.ivmEngine.update(deltaTime / 1000, elapsed, this.mouseX, this.mouseY, this.mouseActive);
    }

    this.render(elapsed);

    this.animationId = requestAnimationFrame(this.animate);
  };

  /**
   * Render a frame
   */
  private render(time: number): void {
    const { ctx } = this;
    const { sideLength, backgroundColor, strokeColor, strokeWidth, baseOpacity } = this.config;
    const height = sideLength * SQRT3 / 2;

    // Clear
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, this.width, this.height);

    // Animation context
    const animContext: AnimationContext = {
      time,
      mouseX: this.mouseX,
      mouseY: this.mouseY,
      mouseActive: this.mouseActive,
    };

    // Get animation function (unless using cascade or ivm)
    const isCascade = this.config.animation === 'cascade';
    const isIvm = this.config.animation === 'ivm';
    const animFn = (isCascade || isIvm) ? null : (animations[this.config.animation as AnimationType] || animations.swarm);

    // Render triangles
    for (const tri of this.triangles) {
      let opacity: number;
      let scale: number;
      let scaleX: number;
      let rotationOffset: number;
      let fillColor: string = tri.color;

      if (isIvm) {
        // IVM simulation mode: per-triangle state-driven visuals
        const v = this.ivmEngine.getVisual(tri.index);
        opacity = v.opacity;
        scale = v.scale;
        scaleX = 1;
        rotationOffset = v.rotation;
        if (v.color) fillColor = v.color;
      } else if (isCascade) {
        // Cascade mode: 3D flip with scaleX for Z-rotation effect
        const cascadeState = this.cascadeSystem.getState(tri.index);
        opacity = cascadeState.opacity;
        scale = 1;
        scaleX = cascadeState.scaleX;
        rotationOffset = 0;
      } else {
        // Standard animation modes
        const pos: TrianglePosition = {
          x: tri.normalizedX,
          y: tri.normalizedY,
          index: tri.index,
        };
        const state = animFn!(pos, animContext, this.animationConfig);
        opacity = state.opacity;
        scale = state.scale;
        scaleX = 1;
        rotationOffset = state.rotationOffset;
      }

      // Skip nearly invisible triangles
      if (opacity * baseOpacity < 0.01) continue;
      if (scale < 0.01) continue;

      // Calculate scaled vertices
      const halfSide = (sideLength * scale) / 2;
      const scaledHeight = height * scale;

      ctx.save();
      ctx.translate(tri.centerX, tri.centerY);

      // Apply scaleX for 3D flip effect (Z-axis rotation simulation)
      if (scaleX !== 1) {
        ctx.scale(scaleX, 1);
      }

      if (rotationOffset !== 0) {
        ctx.rotate((rotationOffset * Math.PI) / 180);
      }

      // Draw triangle
      ctx.beginPath();
      if (tri.orientation === 'up') {
        ctx.moveTo(0, -scaledHeight * (2 / 3));
        ctx.lineTo(-halfSide, scaledHeight / 3);
        ctx.lineTo(halfSide, scaledHeight / 3);
      } else {
        ctx.moveTo(0, scaledHeight * (2 / 3));
        ctx.lineTo(halfSide, -scaledHeight / 3);
        ctx.lineTo(-halfSide, -scaledHeight / 3);
      }
      ctx.closePath();

      // Fill with animated opacity
      ctx.globalAlpha = opacity * baseOpacity;
      ctx.fillStyle = fillColor;
      ctx.fill();

      // Stroke
      if (strokeWidth > 0) {
        ctx.globalAlpha = opacity * baseOpacity * 0.5;
        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = strokeWidth;
        ctx.stroke();
      }

      ctx.restore();
    }
  }

  /**
   * Update configuration
   */
  setConfig(config: Partial<BackgroundConfig>): void {
    this.config = { ...this.config, ...config };
    if (config.animationConfig) {
      this.animationConfig = { ...this.animationConfig, ...config.animationConfig };
    }
    if (config.cascadeConfig) {
      this.cascadeSystem.setConfig(config.cascadeConfig);
    }
    if (config.ivmPreset !== undefined) {
      const preset = config.ivmPreset ? IVM_PRESET_MAP.get(config.ivmPreset) ?? null : null;
      this.ivmEngine.setPreset(preset);
    }
    if (config.ivmFilter !== undefined) {
      const filter = config.ivmFilter ? IVM_FILTER_MAP.get(config.ivmFilter) ?? null : null;
      this.ivmEngine.setFilter(filter);
    }
    this.generateGrid();
  }

  /**
   * Set animation type
   */
  setAnimation(type: AnimationType | 'cascade' | 'ivm'): void {
    this.config.animation = type;
  }

  /**
   * Get current FPS
   */
  getFPS(): number {
    return this.fps;
  }

  /**
   * Get triangle count
   */
  getTriangleCount(): number {
    return this.triangles.length;
  }

  /**
   * Destroy and clean up
   */
  destroy(): void {
    this.stop();
  }
}

// Preset configurations
export const presets: Record<string, Partial<BackgroundConfig>> = {
  swarmDark: {
    animation: 'swarm',
    backgroundColor: '#0a0f1a',
    colors: [
      TRINITY_PALETTE.lightBlue,
      TRINITY_PALETTE.mediumBlue,
      TRINITY_PALETTE.darkBlue,
      TRINITY_PALETTE.deepBlue,
    ],
    animationConfig: {
      speed: 0.8,
      intensity: 0.6,
      noiseScale: 2.5,
    },
  },
  waveLight: {
    animation: 'wave',
    backgroundColor: '#f8fafc',
    colors: ['#bfdbfe', '#93c5fd', '#60a5fa', '#3b82f6'],
    strokeColor: 'rgba(148, 163, 184, 0.3)',
    animationConfig: {
      speed: 1.2,
      intensity: 0.5,
    },
  },
  pulseEnergy: {
    animation: 'pulse',
    backgroundColor: '#0c0a1a',
    colors: ['#a855f7', '#8b5cf6', '#7c3aed', '#6d28d9'],
    animationConfig: {
      speed: 1.5,
      intensity: 0.7,
    },
  },
  vortexPortal: {
    animation: 'vortex',
    backgroundColor: '#0f172a',
    colors: ['#22d3ee', '#06b6d4', '#0891b2', '#0e7490'],
    animationConfig: {
      speed: 0.6,
      intensity: 0.8,
    },
  },
  breatheCalm: {
    animation: 'breathe',
    backgroundColor: '#1a1a2e',
    colors: ['#4ade80', '#22c55e', '#16a34a', '#15803d'],
    animationConfig: {
      speed: 0.4,
      intensity: 0.4,
    },
  },
  flowField: {
    animation: 'flow',
    backgroundColor: '#0f0f1a',
    colors: [
      TRINITY_PALETTE.lightBlue,
      TRINITY_PALETTE.mediumBlue,
      '#f472b6',
      '#a855f7',
    ],
    animationConfig: {
      speed: 0.7,
      intensity: 0.6,
      noiseScale: 3,
    },
  },
  // Cascade presets with different coverage patterns
  // Gap creates hexagonal negative space pattern
  cascadeVignette: {
    animation: 'cascade',
    backgroundColor: '#0a0f1a',
    gap: 10,
    colors: [
      TRINITY_PALETTE.lightBlue,
      TRINITY_PALETTE.mediumBlue,
      TRINITY_PALETTE.darkBlue,
      TRINITY_PALETTE.deepBlue,
    ],
    cascadeConfig: {
      coverage: 'vignette',
      coverageFalloff: 0.15,
    },
  },
  cascadeTop: {
    animation: 'cascade',
    backgroundColor: '#0a0f1a',
    gap: 10,
    colors: [
      TRINITY_PALETTE.lightBlue,
      TRINITY_PALETTE.mediumBlue,
      TRINITY_PALETTE.darkBlue,
    ],
    cascadeConfig: {
      coverage: 'top',
      coverageFalloff: 0.2,
    },
  },
  cascadeBottom: {
    animation: 'cascade',
    backgroundColor: '#0a0f1a',
    gap: 10,
    colors: [
      TRINITY_PALETTE.lightBlue,
      TRINITY_PALETTE.mediumBlue,
      TRINITY_PALETTE.darkBlue,
    ],
    cascadeConfig: {
      coverage: 'bottom',
      coverageFalloff: 0.2,
    },
  },
  cascadeLeft: {
    animation: 'cascade',
    backgroundColor: '#0a0f1a',
    gap: 10,
    colors: [
      TRINITY_PALETTE.lightBlue,
      TRINITY_PALETTE.mediumBlue,
      TRINITY_PALETTE.darkBlue,
    ],
    cascadeConfig: {
      coverage: 'left',
      coverageFalloff: 0.2,
    },
  },
  cascadeRight: {
    animation: 'cascade',
    backgroundColor: '#0a0f1a',
    gap: 10,
    colors: [
      TRINITY_PALETTE.lightBlue,
      TRINITY_PALETTE.mediumBlue,
      TRINITY_PALETTE.darkBlue,
    ],
    cascadeConfig: {
      coverage: 'right',
      coverageFalloff: 0.2,
    },
  },
  cascadeCenter: {
    animation: 'cascade',
    backgroundColor: '#0a0f1a',
    gap: 10,
    colors: [
      TRINITY_PALETTE.lightBlue,
      TRINITY_PALETTE.mediumBlue,
      TRINITY_PALETTE.darkBlue,
    ],
    cascadeConfig: {
      coverage: 'center',
      coverageFalloff: 0.25,
    },
  },
};
